import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import { getSubstatusOptionsForStatusRow } from '../../utils/statusTriggerUtils';
import StatusLevelTriggerBlock from './StatusLevelTriggerBlock';
import SubStatusTriggerBlock from './SubStatusTriggerBlock';

export default function StatusOptionTriggersAccordion({
  statusRow,
  statusOptionName,
  subStatusFields = [],
  expanded = false,
  disabled = false,
  workspaceId,
  isSaving = false,
  onToggle,
  onSaveStatusTriggers,
  onSaveSubstatusTriggers,
}) {
  const { semantic, radiusPx, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.automations.statusTriggers;
  const panelId = `trigger-option-${String(statusOptionName).replace(/\s+/g, '-')}`;

  return (
    <Accordion
      expanded={expanded}
      disabled={disabled}
      onChange={(_, isExpanded) => onToggle?.(statusOptionName, isExpanded)}
      disableGutters
      elevation={0}
      sx={{
        mb: 0.75,
        border: `1px solid ${semantic.borders.border}`,
        borderRadius: `${radiusPx.md} !important`,
        '&:before': { display: 'none' },
        bgcolor: semantic.surfaces.surface,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${panelId}-content`}
        id={`${panelId}-header`}
        sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.75 } }}
      >
        <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
          {statusOptionName}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
        <Stack spacing={2}>
          <StatusLevelTriggerBlock
            statusRow={statusRow}
            statusOptionName={statusOptionName}
            workspaceId={workspaceId}
            disabled={disabled}
            isSaving={isSaving}
            onSaveTriggers={(triggers) => onSaveStatusTriggers?.(statusRow, triggers)}
          />

          {subStatusFields.length > 0 ? (
            <>
              <Divider />
              <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 }}>
                {copy.subStatusTriggersTitle}
              </Typography>
              <Stack spacing={1.25}>
                {subStatusFields.map((subField) =>
                  getSubstatusOptionsForStatusRow(subField, statusOptionName).map(
                    (subOptionName) => (
                      <SubStatusTriggerBlock
                        key={`${subField.key_name}-${statusOptionName}-${subOptionName}`}
                        statusRow={statusRow}
                        statusOptionName={statusOptionName}
                        subStatusField={subField}
                        subOptionName={subOptionName}
                        workspaceId={workspaceId}
                        disabled={disabled}
                        isSaving={isSaving}
                        onSaveSubstatusTriggers={(subName, subKey, triggers) =>
                          onSaveSubstatusTriggers?.(statusRow, subName, subKey, triggers)
                        }
                      />
                    )
                  )
                )}
              </Stack>
            </>
          ) : null}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
